import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  BarChart3, ChevronDown, ChevronRight, Copy, Edit3, FileDown, FileSpreadsheet, Grid3x3, LayoutGrid, Link2,
  Map as MapIcon, Minus, Plus, Rows, Save, Table2, Trash2, Type, X,
} from 'lucide-react';
import { localeTag } from '@/i18n/format';
import { useMapStore } from '@/store/map.store';
import { useToast } from '@/components/common/Toast';
import reportService from '@/services/report.service';
import { useStudio } from './store';
import { Builder } from './Builder';
import { VisualBody, type TableSelection } from './VisualBody';
import { CombineDialog, ExportDialog, SaveDialog } from './Dialogs';
import { pdfFit } from './fit';
import { companyLogo, snapshotMap, thumbnailOf } from './snapshot';
import {
  defaultAgg, newChart, newPivot, pivotData, resolveDataset, tableData, toBundle, visualsOf, type Words,
} from './engine';
import {
  PAGE, PALETTE, newId, type Aggregation, type ChartVisual, type Placement, type ReportDoc, type Visual,
} from './model';
import type { BundleLabels } from './export/bundle';

const GRID = 8;
const snap = (n: number) => Math.round(n / GRID) * GRID;

/**
 * The report studio: an analysis' tables on a page-wide canvas, and whatever charts, cross tables and notes the
 * user makes of them — dragged, resized, saved into a workspace, opened again, and written out as a PDF (real
 * text and drawings, tables turned or cut to fit) or an Excel workbook (styled sheets, real charts).
 */
export const ReportStudio: React.FC = () => {
  const doc = useStudio((s) => s.doc);
  if (!doc) return null;
  return <Studio doc={doc} />;
};

const Studio: React.FC<{ doc: ReportDoc }> = ({ doc }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { change, close, saved, dirty, markSaved } = useStudio();
  const map = useMapStore((s) => s.mapInstance);
  const locale = localeTag();

  const [selected, setSelected] = useState<string | null>(null);
  const [tableSel, setTableSel] = useState<TableSelection | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [dialog, setDialog] = useState<null | 'save' | 'saveAs' | 'combine' | 'pdf' | 'xlsx'>(null);
  const [retaking, setRetaking] = useState(false);
  const [guides, setGuides] = useState(true);
  const [openDs, setOpenDs] = useState<string | null>(null);
  /** While a card is dragged or resized: where the others would go if it were dropped here. */
  const [preview, setPreview] = useState<Placement[] | null>(null);

  const words = useMemo<Words & { total: string }>(() => ({
    agg: (a: Aggregation) => t(`studio.agg_${a}`),
    of: (agg: string, field: string) => t('studio.agg_of', { agg, field }),
    total: t('studio.total'),
  }), [t]);
  const originLabels = useMemo(() => ({
    table: t('studio.origin_table'), point: t('studio.origin_point'), travel: t('studio.origin_travel'), time: t('studio.origin_time'),
  }), [t]);

  const page = PAGE[doc.orientation];
  const selectedVisual = doc.visuals.find((v) => v.id === selected) ?? null;

  /* ── canvas size & zoom ── */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    const fit = () => {
      const el = scrollRef.current;
      if (!el) return;
      setZoom(Math.min(1.25, Math.max(0.4, (el.clientWidth - 64) / page.width)));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [page.width]);

  const bottom = Math.max(page.height, ...doc.layout.map((p) => p.y + p.h)) + 160;
  const pages = Math.ceil((bottom - 160) / page.height);

  /* ── changing the report ── */
  const setLayout = useCallback((fn: (l: Placement[]) => Placement[]) => change((d) => ({ ...d, layout: fn(d.layout) })), [change]);
  const setVisual = useCallback((v: Visual) => change((d) => ({ ...d, visuals: d.visuals.map((x) => (x.id === v.id ? v : x)) })), [change]);

  const place = useCallback((d: ReportDoc, id: string, w: number, h: number, near?: string): Placement => {
    const width = PAGE[d.orientation].width;
    const anchor = near ? d.layout.find((p) => p.id === near) : undefined;
    let x = anchor ? anchor.x + anchor.w + 16 : 0;
    let y = anchor ? anchor.y : Math.max(0, ...d.layout.map((p) => p.y + p.h)) + 16;
    if (x + w > width) {
      x = 0;
      y = anchor ? anchor.y + anchor.h + 16 : y;
    }
    return { id, x, y, w: Math.min(w, width), h };
  }, []);

  const addVisual = useCallback((v: Visual, w: number, h: number, near?: string) => {
    change((d) => {
      const p = place(d, v.id, w, h, near);
      return { ...d, visuals: [...d.visuals, v], layout: settle([...d.layout, p], p.id) };
    });
    setSelected(v.id);
  }, [change, place]);

  const addChart = (datasetId: string) => {
    const r = resolveDataset(doc, datasetId, originLabels);
    const ds = doc.datasets.find((d) => d.id === datasetId);
    addVisual(newChart(newId('chart'), datasetId, r.fields, ds?.title ?? t('studio.kind_chart')), 440, 300, doc.visuals.find((v) => 'datasetId' in v && v.datasetId === datasetId)?.id);
  };
  const addPivot = (datasetId: string) => {
    const r = resolveDataset(doc, datasetId, originLabels);
    const ds = doc.datasets.find((d) => d.id === datasetId);
    addVisual(newPivot(newId('pivot'), datasetId, r.fields, `${ds?.title ?? ''} — ${t('studio.kind_pivot')}`), 480, 280);
  };
  const addTable = (datasetId: string) => {
    const ds = doc.datasets.find((d) => d.id === datasetId);
    addVisual({ id: newId('table'), kind: 'table', title: ds?.title ?? '', datasetId, filters: [], excludeTotals: false, columns: [] }, 480, 260);
  };
  const addText = () => addVisual({ id: newId('text'), kind: 'text', title: t('studio.kind_text'), text: '' }, 480, 140);
  const addMap = async () => {
    if (!doc.map) {
      setRetaking(true);
      const m = await snapshotMap(map);
      setRetaking(false);
      if (!m) {
        addToast(t('studio.map_failed'), 'error');
        return;
      }
      change((d) => ({ ...d, map: m }));
    }
    addVisual({ id: newId('map'), kind: 'map', title: t('studio.kind_map') }, 560, 360);
  };

  const remove = (id: string) => {
    change((d) => ({ ...d, visuals: d.visuals.filter((v) => v.id !== id), layout: compact(d.layout.filter((p) => p.id !== id)) }));
    if (selected === id) setSelected(null);
  };
  const duplicate = (id: string) => {
    const v = doc.visuals.find((x) => x.id === id);
    const p = doc.layout.find((x) => x.id === id);
    if (!v || !p) return;
    const copy = { ...structuredClone(v), id: newId(v.kind) } as Visual;
    addVisual(copy, p.w, p.h, id);
  };

  /** A chart of the rows and columns ticked in a table. */
  const chartFromSelection = () => {
    if (!tableSel) return;
    const tv = doc.visuals.find((v) => v.id === tableSel.visualId);
    if (!tv || tv.kind !== 'table') return;
    const table = tableData(doc, tv, originLabels);
    const r = resolveDataset(doc, tv.datasetId, originLabels);
    const labelKey = table.columns[0]?.key ?? '';
    const cols = [...tableSel.cols].sort((a, b) => a - b).map((i) => table.columns[i]).filter((c) => c && c.format !== 'text');
    const measureCols = cols.length ? cols : table.columns.slice(1).filter((c) => c.format !== 'text');
    const rowValues = [...tableSel.rows].sort((a, b) => a - b).map((i) => table.rows[i]?.[0]).filter((v) => v != null).map(String);
    const byColumns = measureCols.length >= 3 && (rowValues.length || table.rows.length) <= 8;
    const chart: ChartVisual = {
      id: newId('chart'), kind: 'chart', title: tv.title, datasetId: tv.datasetId,
      filters: rowValues.length ? [{ field: labelKey, values: rowValues }] : [],
      excludeTotals: rowValues.length === 0,
      chartType: 'column',
      category: labelKey, series: '',
      measures: measureCols.map((c) => ({ field: c.key, agg: defaultAgg(r.fields.find((f) => f.key === c.key)) })),
      orientation: byColumns ? 'columns' : 'rows',
      sort: 'none', topN: 0, showLabels: false, palette: PALETTE,
    };
    addVisual(chart, 460, 300, tv.id);
    setTableSel(null);
  };

  /** Lay every card out again: page-wide rows, in reading order. */
  const arrange = () => {
    setLayout((l) => {
      const sorted = [...l].sort((a, b) => a.y - b.y || a.x - b.x);
      const out: Placement[] = [];
      let x = 0;
      let y = 0;
      let rowH = 0;
      for (const p of sorted) {
        const w = Math.min(p.w, page.width);
        if (x > 0 && x + w > page.width) {
          x = 0;
          y += rowH + 16;
          rowH = 0;
        }
        out.push({ ...p, x, y, w });
        x += w + 16;
        rowH = Math.max(rowH, p.h);
      }
      return compact(out);
    });
  };

  /* ── map snapshot ── */
  const retakeMap = async () => {
    setRetaking(true);
    const m = await snapshotMap(map);
    setRetaking(false);
    if (m) change((d) => ({ ...d, map: m }));
    else addToast(t('studio.map_failed'), 'error');
  };

  /* ── combining tables ── */
  const combine = (mode: 'join' | 'append', sources: string[], key: string, title: string) => {
    const id = newId(mode);
    const table: Visual = { id: newId('table'), kind: 'table', title, datasetId: id, filters: [], excludeTotals: false, columns: [] };
    change((d) => {
      const withData: ReportDoc = { ...d, datasets: [...d.datasets, mode === 'join' ? { id, kind: 'join', title, sources, key } : { id, kind: 'append', title, sources }] };
      const p = place(withData, table.id, PAGE[d.orientation].width, 300);
      return { ...withData, visuals: [...withData.visuals, table], layout: settle([...withData.layout, p], p.id) };
    });
    setDialog(null);
    setOpenDs(id);
    setSelected(table.id);
  };
  const dropDataset = (id: string) => {
    const used = visualsOf(doc, id).length > 0 || doc.datasets.some((d) => d.kind !== 'source' && d.sources.includes(id));
    if (used) {
      addToast(t('studio.dataset_in_use'), 'warning');
      return;
    }
    change((d) => ({ ...d, datasets: d.datasets.filter((x) => x.id !== id) }));
  };

  /* ── saving ── */
  const save = async (workspace: string, name: string, asNew: boolean) => {
    const next = { ...useStudio.getState().doc!, name };
    const thumbnail = await thumbnailOf(next.map);
    const result = !asNew && saved && saved.workspace === workspace
      ? await reportService.update(workspace, saved.id, next, thumbnail)
      : await reportService.create(workspace, next, thumbnail);
    change((d) => ({ ...d, name }));
    markSaved({ workspace, id: result.id, canUpdate: result.canUpdate });
    queryClient.invalidateQueries({ queryKey: ['workspaceReports', workspace] });
    addToast(t('studio.saved'), 'success');
    setDialog(null);
  };
  const quickSave = async () => {
    if (saved && saved.canUpdate) {
      try {
        await save(saved.workspace, doc.name, false);
      } catch (e: any) {
        addToast(e?.message ?? String(e), 'error');
      }
    } else {
      setDialog('save');
    }
  };

  const tryClose = () => {
    if (dirty && !window.confirm(t('studio.close_unsaved'))) return;
    close();
  };

  /* ── exporting ── */
  const labels = useMemo<BundleLabels>(() => ({
    page: t('studio.pdf_page'),
    generated: t('studio.pdf_generated'),
    continued: t('studio.pdf_continued'),
    columnsPart: t('studio.pdf_columns_part'),
    transposed: t('studio.pdf_transposed'),
    contents: t('studio.xlsx_contents'),
    chartData: t('studio.xlsx_chart_data'),
    map: t('studio.kind_map'),
    noData: t('studio.no_rows'),
  }), [t]);

  const makeBundle = useCallback(async (include: Set<string>, orientation: 'portrait' | 'landscape') => {
    const current = useStudio.getState().doc!;
    return toBundle(current, {
      include, labels, locale, words, originLabels, orientation,
      companyLogo: await companyLogo(),
      subtitle: current.map ? t('studio.subtitle_taken', { date: new Date(current.map.takenAt).toLocaleString(locale) }) : undefined,
      mapCaption: current.map?.layers.length ? `${t('studio.map_layers')}: ${current.map.layers.join(', ')}` : undefined,
    });
  }, [labels, locale, words, originLabels, t]);

  /* ── keyboard ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected && !dialog) {
        e.preventDefault();
        remove(selected);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        quickSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const headerBtn = 'flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] rounded border border-gn text-gn-primary hover:bg-gn-tertiary transition-colors disabled:opacity-50';

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-gn-primary text-gn-primary">
      {/* ── header ── */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gn bg-gn-secondary shrink-0">
        {renaming ? (
          <input autoFocus value={doc.name} onChange={(e) => change((d) => ({ ...d, name: e.target.value }))}
                 onBlur={() => setRenaming(false)} onKeyDown={(e) => { if (e.key === 'Enter') setRenaming(false); }}
                 className="px-2 py-1 text-sm font-semibold bg-gn-input border border-gn-blue rounded w-80 focus:outline-none" />
        ) : (
          <button type="button" onClick={() => setRenaming(true)} className="flex items-center gap-1.5 text-sm font-semibold hover:text-gn-blue" title={t('studio.rename')}>
            {doc.name} <Edit3 size={12} className="text-gn-secondary" />
          </button>
        )}
        <span className="text-[10px] text-gn-muted">{saved ? (dirty ? t('studio.unsaved_changes') : t('studio.all_saved')) : t('studio.not_saved')}</span>

        <div className="flex-1" />

        <div className="flex items-center gap-1 pr-2 mr-1 border-r border-gn">
          <button type="button" className={headerBtn} onClick={addText}><Type size={13} /> {t('studio.add_text')}</button>
          <button type="button" className={headerBtn} onClick={addMap} disabled={retaking}><MapIcon size={13} /> {t('studio.add_map')}</button>
          <button type="button" className={headerBtn} onClick={() => setDialog('combine')}><Link2 size={13} /> {t('studio.combine_tables')}</button>
          <button type="button" className={headerBtn} onClick={arrange} title={t('studio.arrange_hint')}><LayoutGrid size={13} /> {t('studio.arrange')}</button>
          <button type="button" className={`${headerBtn} ${guides ? 'bg-gn-tertiary' : ''}`} onClick={() => setGuides(!guides)} title={t('studio.page_guides_hint')}>
            <Rows size={13} /> {t('studio.page_guides')}
          </button>
          <select value={doc.orientation} onChange={(e) => change((d) => reflow(d, e.target.value as ReportDoc['orientation']))}
                  className="px-2 py-1.5 text-[11px] rounded border border-gn bg-gn-input" title={t('studio.orientation')}>
            <option value="landscape">{t('studio.landscape')}</option>
            <option value="portrait">{t('studio.portrait')}</option>
          </select>
        </div>

        <button type="button" className={headerBtn} onClick={() => setDialog('pdf')}><FileDown size={13} /> PDF</button>
        <button type="button" className={headerBtn} onClick={() => setDialog('xlsx')}><FileSpreadsheet size={13} /> Excel</button>
        {saved && (
          <button type="button" className={headerBtn} onClick={() => setDialog('saveAs')}><Copy size={13} /> {t('studio.save_as')}</button>
        )}
        <button type="button" onClick={quickSave}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded bg-gn-blue text-gn-inverse hover:opacity-90">
          <Save size={13} /> {t('studio.save')}
        </button>
        <button type="button" onClick={tryClose} className="p-1.5 text-gn-secondary hover:text-gn-primary" title={t('studio.close')}><X size={16} /></button>
      </div>

      <div className="flex-1 min-h-0 flex">
        {/* ── data ── */}
        <aside className="w-64 shrink-0 border-r border-gn bg-gn-secondary overflow-y-auto">
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gn-secondary">{t('studio.data')}</div>
          {doc.datasets.map((d) => {
            const fields = resolveDataset(doc, d.id, originLabels).fields;
            const open = openDs === d.id;
            return (
              <div key={d.id} className="border-t border-gn-light">
                <div className="flex items-center gap-1 px-2 py-1.5 hover:bg-gn-tertiary">
                  <button type="button" onClick={() => setOpenDs(open ? null : d.id)} className="flex-1 min-w-0 flex items-center gap-1 text-left">
                    {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    {d.kind !== 'source' && <Link2 size={11} className="text-gn-blue shrink-0" />}
                    <span className="text-[11px] truncate" title={d.title}>{d.title}</span>
                  </button>
                  <button type="button" className="p-0.5 text-gn-secondary hover:text-gn-blue" title={t('studio.add_chart')} onClick={() => addChart(d.id)}><BarChart3 size={13} /></button>
                  <button type="button" className="p-0.5 text-gn-secondary hover:text-gn-blue" title={t('studio.add_pivot')} onClick={() => addPivot(d.id)}><Grid3x3 size={13} /></button>
                  <button type="button" className="p-0.5 text-gn-secondary hover:text-gn-blue" title={t('studio.add_table')} onClick={() => addTable(d.id)}><Table2 size={13} /></button>
                  {d.kind !== 'source' && (
                    <button type="button" className="p-0.5 text-gn-secondary hover:text-gn-danger" title={t('studio.remove')} onClick={() => dropDataset(d.id)}><Trash2 size={12} /></button>
                  )}
                </div>
                {open && (
                  <ul className="pb-1.5 pl-6 pr-2 space-y-0.5">
                    {fields.map((f) => (
                      <li key={f.key} className="flex items-center gap-1 text-[10px] text-gn-secondary">
                        <span className="w-4 font-mono text-gn-muted">{f.format === 'text' ? 'Aa' : f.format === 'percent' ? '%' : '#'}</span>
                        <span className="truncate" title={f.label}>{f.label}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </aside>

        {/* ── canvas ── */}
        <div ref={scrollRef} className="flex-1 min-w-0 overflow-auto" onMouseDown={() => { setSelected(null); }}
             style={{ backgroundImage: 'radial-gradient(circle, var(--gn-border) 1px, transparent 1px)', backgroundSize: '24px 24px' }}>
          <div style={{ width: (page.width + 64) * zoom, height: (bottom + 64) * zoom }} className="relative mx-auto">
            <div className="absolute origin-top-left" style={{ left: 32 * zoom, top: 32 * zoom, width: page.width, height: bottom, transform: `scale(${zoom})` }}>
              {/* the page: its width, and where each page ends */}
              <div className="absolute inset-y-0 left-0 bg-gn-primary/40 border-x border-dashed border-gn-blue/60 pointer-events-none" style={{ width: page.width }} />
              {guides && Array.from({ length: pages }, (_, i) => (
                <div key={i} className="absolute left-[-24px] right-[-24px] z-30 border-t-2 border-dashed border-gn-blue/70 pointer-events-none" style={{ top: (i + 1) * page.height }}>
                  <span className="absolute -top-[18px] left-6 text-[10px] px-1.5 rounded-t bg-gn-blue text-gn-inverse">
                    {t('studio.page_end', { page: i + 1 })}
                  </span>
                </div>
              ))}
              {doc.visuals.map((v) => {
                const p = (preview ?? doc.layout).find((x) => x.id === v.id);
                if (!p) return null;
                return (
                  <Card key={v.id} p={p} zoom={zoom} width={page.width} selected={selected === v.id}
                        title={v.title || t(`studio.kind_${v.kind}`)}
                        badge={v.kind === 'table' || v.kind === 'pivot' ? <FitBadge doc={doc} visual={v} words={words} originLabels={originLabels} locale={locale} /> : null}
                        extra={tableSel?.visualId === v.id ? (
                          <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={chartFromSelection}
                                  className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-gn-blue text-gn-inverse">
                            <BarChart3 size={11} /> {t('studio.chart_from_selection')}
                          </button>
                        ) : null}
                        onSelect={() => setSelected(v.id)}
                        onLive={(cur) => setPreview(cur ? settle(doc.layout.map((q) => (q.id === v.id ? cur : q)), v.id) : null)}
                        onMove={(x, y) => setLayout((l) => settle(l.map((q) => (q.id === v.id ? { ...q, x, y } : q)), v.id))}
                        onResize={(w, h) => setLayout((l) => settle(l.map((q) => (q.id === v.id ? { ...q, w, h } : q)), v.id))}
                        onDuplicate={() => duplicate(v.id)}
                        onRemove={() => remove(v.id)}>
                    <VisualBody doc={doc} visual={v} locale={locale} words={words} originLabels={originLabels}
                                selection={tableSel} onSelection={setTableSel}
                                onText={(id, text) => change((d) => ({ ...d, visuals: d.visuals.map((x) => (x.id === id && x.kind === 'text' ? { ...x, text } : x)) }))} />
                  </Card>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── builder ── */}
        <aside className="w-80 shrink-0 border-l border-gn bg-gn-secondary overflow-y-auto">
          {selectedVisual ? (
            <Builder doc={doc} visual={selectedVisual} originLabels={originLabels} onChange={setVisual}
                     onRetakeMap={retakeMap} retaking={retaking} />
          ) : (
            <div className="p-4 space-y-3 text-[11px] text-gn-secondary">
              <p className="font-semibold text-gn-primary">{t('studio.start_title')}</p>
              <ul className="list-disc pl-4 space-y-1.5">
                <li>{t('studio.start_select')}</li>
                <li>{t('studio.start_add')}</li>
                <li>{t('studio.start_pick')}</li>
                <li>{t('studio.start_combine')}</li>
                <li>{t('studio.start_guides')}</li>
              </ul>
            </div>
          )}
        </aside>
      </div>

      {(dialog === 'save' || dialog === 'saveAs') && (
        <SaveDialog name={doc.name} current={saved?.workspace} onCancel={() => setDialog(null)}
                    onSave={(ws, name) => save(ws, name, dialog === 'saveAs')} />
      )}
      {dialog === 'combine' && <CombineDialog doc={doc} onCancel={() => setDialog(null)} onCombine={combine} />}
      {(dialog === 'pdf' || dialog === 'xlsx') && <ExportDialog doc={doc} format={dialog} onClose={() => setDialog(null)} bundle={makeBundle} />}
    </div>
  );
};

/* ───────────── a card on the canvas ───────────── */

const Card: React.FC<{
  p: Placement;
  zoom: number;
  width: number;
  selected: boolean;
  title: string;
  badge?: React.ReactNode;
  extra?: React.ReactNode;
  onSelect: () => void;
  onLive: (cur: Placement | null) => void;
  onMove: (x: number, y: number) => void;
  onResize: (w: number, h: number) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  children: React.ReactNode;
}> = ({ p, zoom, width, selected, title, badge, extra, onSelect, onLive, onMove, onResize, onDuplicate, onRemove, children }) => {
  const { t } = useTranslation();
  const [live, setLive] = useState<Placement | null>(null);
  const shown = live ?? p;

  const drag = (kind: 'move' | 'resize') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    const sx = e.clientX;
    const sy = e.clientY;
    let cur = { ...p };
    const onMoveEv = (ev: MouseEvent) => {
      const dx = (ev.clientX - sx) / zoom;
      const dy = (ev.clientY - sy) / zoom;
      if (kind === 'move') {
        cur = { ...p, x: Math.max(0, Math.min(width - p.w, snap(p.x + dx))), y: Math.max(0, snap(p.y + dy)) };
      } else {
        cur = { ...p, w: Math.max(160, Math.min(width - p.x, snap(p.w + dx))), h: Math.max(100, snap(p.h + dy)) };
      }
      setLive(cur);
      onLive(cur);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMoveEv);
      window.removeEventListener('mouseup', onUp);
      setLive(null);
      onLive(null);
      if (kind === 'move') {
        if (cur.x !== p.x || cur.y !== p.y) onMove(cur.x, cur.y);
      } else if (cur.w !== p.w || cur.h !== p.h) onResize(cur.w, cur.h);
    };
    window.addEventListener('mousemove', onMoveEv);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div className={`absolute flex flex-col rounded-lg border bg-gn-secondary shadow-lg overflow-hidden ${selected ? 'border-gn-blue ring-2 ring-blue-500/40' : 'border-gn'} ${live ? 'opacity-90 z-20' : 'z-10'}`}
         style={{ left: shown.x, top: shown.y, width: shown.w, height: shown.h }}
         onMouseDown={(e) => { e.stopPropagation(); onSelect(); }}>
      <div className="flex items-center gap-1.5 px-2 py-1 border-b border-gn bg-gn-tertiary cursor-move select-none shrink-0" onMouseDown={drag('move')}>
        <span className="flex-1 min-w-0 text-[11px] font-semibold truncate" title={title}>{title}</span>
        {extra}
        {badge}
        <button type="button" className="p-0.5 text-gn-secondary hover:text-gn-blue" title={t('studio.duplicate')}
                onMouseDown={(e) => e.stopPropagation()} onClick={onDuplicate}><Plus size={12} /></button>
        <button type="button" className="p-0.5 text-gn-secondary hover:text-gn-danger" title={t('studio.remove')}
                onMouseDown={(e) => e.stopPropagation()} onClick={onRemove}><Minus size={12} /></button>
      </div>
      <div className="flex-1 min-h-0">{children}</div>
      <div className="absolute right-0 bottom-0 w-3.5 h-3.5 cursor-se-resize" onMouseDown={drag('resize')}
           style={{ background: 'linear-gradient(135deg, transparent 50%, var(--gn-border) 50%)' }} />
    </div>
  );
};

/** On the table: how it will go onto the PDF page. */
const FitBadge: React.FC<{ doc: ReportDoc; visual: Visual; words: Words & { total: string }; originLabels: Record<string, string>; locale: string }> = ({
  doc, visual, words, originLabels, locale,
}) => {
  const { t } = useTranslation();
  const { datasets, orientation } = doc;
  const fit = useMemo(() => {
    if (visual.kind === 'table') return pdfFit(tableData({ datasets }, visual, originLabels), orientation, locale);
    if (visual.kind === 'pivot') return pdfFit(pivotData({ datasets }, visual, words, originLabels), orientation, locale);
    return null;
  }, [datasets, orientation, visual, words, originLabels, locale]);
  if (!fit || fit.kind === 'fits') return null;
  const text = fit.kind === 'transposed'
    ? (fit.parts > 1 ? t('studio.fit_transposed_parts', { parts: fit.parts }) : t('studio.fit_transposed'))
    : t('studio.fit_split', { parts: fit.parts });
  return (
    <span className="px-1.5 py-0.5 rounded text-[9px] bg-gn-warning-light text-gn-warning whitespace-nowrap" title={t('studio.fit_hint')}>
      PDF: {text}
    </span>
  );
};

/* ───────────── layout helpers ───────────── */

/**
 * Where the moved (or resized, or added) card goes among the others: before every card whose middle is below its
 * own middle, after the rest. Then everything rises, in that order — the others make room and close the gap it left.
 */
function settle(layout: Placement[], movedId: string): Placement[] {
  const moved = layout.find((p) => p.id === movedId);
  const others = layout.filter((p) => p.id !== movedId).sort((a, b) => a.y - b.y || a.x - b.x);
  if (!moved) return compact(others);
  const at = others.findIndex((q) => moved.y + moved.h / 2 < q.y + q.h / 2);
  const order = at < 0 ? [...others, moved] : [...others.slice(0, at), moved, ...others.slice(at)];
  const out = compact(order, true);
  return layout.map((p) => out.find((q) => q.id === p.id) ?? p);
}

/**
 * Gravity, as Veramenta's dashboards have it: every card rises until it meets the card above it (or the top of
 * the page), in reading order — a card moved away or removed leaves no hole.
 */
function compact(layout: Placement[], ordered = false): Placement[] {
  const gap = 16;
  const order = layout.map((p) => ({ ...p }));
  if (!ordered) order.sort((a, b) => a.y - b.y || a.x - b.x);
  const placed: Placement[] = [];
  for (const p of order) {
    let y = 0;
    for (const q of placed) {
      if (p.x < q.x + q.w && p.x + p.w > q.x) y = Math.max(y, q.y + q.h + gap);
    }
    p.y = y;
    placed.push(p);
  }
  return layout.map((p) => placed.find((q) => q.id === p.id) ?? p);
}

/** Another page orientation: cards squeezed into the new width, keeping their order. */
function reflow(d: ReportDoc, orientation: ReportDoc['orientation']): ReportDoc {
  if (d.orientation === orientation) return d;
  const from = PAGE[d.orientation].width;
  const to = PAGE[orientation].width;
  const k = to / from;
  const layout = d.layout.map((p) => ({ ...p, x: snap(p.x * k), w: Math.max(160, snap(p.w * k)) }));
  let out = layout;
  for (const p of layout) out = settle(out, p.id);
  return { ...d, orientation, layout: out };
}
